import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HasRoleDirective } from "../../core/directives/has-role.directive";
import { ITileData } from "../../core/models/landing-fields";
import { AuthRealmService } from "../../core/services/keycloak/auth-realm.service";
import { UserService } from "../../core/services/user.service";
import { MaterialModule } from "../../core/shared/material.module";
import { SharedComponents } from "../shared.module";

const PROJECT_ACTION_ADMIN_ROLES = [
  "accello360-organization-admin",
  "accello360-organization-owner",
  "accello360-global-administrator",
];

/**
 * Component representing a card tile with interactive functionality.
 * The tile can display different types of content and notify parent components of specific actions.
 */
@Component({
  selector: "app-card-tile",
  standalone: true,
  imports: [CommonModule, MaterialModule, HasRoleDirective, ...SharedComponents],
  templateUrl: "./card-tile.component.html",
  styleUrl: "./card-tile.component.scss",
})
export class CardTileComponent implements OnChanges {
  private readonly authRealmService = inject(AuthRealmService);
  private readonly userService = inject(UserService);

  @Input()
  cardData!: ITileData;

  @Input()
  cardType: string = "organization";

  /** Parent sets this while the edit modal is open so a second click cannot fire. */
  @Input()
  editDisabled = false;

  private editClickLocked = false;
  private isFoundationUser = false;

  /**
   * Event emitter to notify parent components of actions performed within the card tile.
   * Emits an object containing the card data, action type, and optional event details.
   */
  @Output()
  onCreateProjectOrganization = new EventEmitter<{
    event: ITileData;
    actionType?: string;
    $event?: Event;
  }>();

  private readonly avatarColors = [
    "#5f2cff",
    "#38369A",
    "#2f76ff",
    "#00434A",
    "#FC404A",
    "#FEC901",
    "#C105B3",
    "#73777E",
  ];

  /**
   * Generates the path to the card's logo image based on its type and logo property.
   * @returns {string} The path to the logo image.
   */
  updateImage() {
    return `assets/images/${this.cardType}/${this.resolveLogoFile(this.cardData.logo)}`;
  }

  /** Prefer description; for projects, fall back to scope when description is missing. */
  get cardDescription(): string {
    if (this.cardData?.description) {
      return this.cardData.description;
    }
    if (this.cardType === "project") {
      return this.cardData?.scope || "";
    }
    return "";
  }

  /** Project cover files are SVGs; stored logos may still be `.jpg`. */
  private resolveLogoFile(logo?: string): string {
    if (!logo) {
      return this.cardType === "project" ? "1.svg" : "";
    }
    if (this.cardType !== "project") {
      return logo;
    }
    const svgLogo = logo.replace(/\.jpe?g$/i, ".svg");
    return svgLogo === "10.svg" ? "1.svg" : svgLogo;
  }

  getInitial(name: string): string {
    if (!name?.trim()) {
      return "";
    }
    return name.trim()[0].toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name?.trim()) {
      return this.avatarColors[0];
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return this.avatarColors[Math.abs(hash) % this.avatarColors.length];
  }

  get isEditLocked(): boolean {
    return this.editDisabled || this.editClickLocked;
  }

  /**
   * Hide Add Users only for `accello360-project-owner` when foundationUser is true.
   * Org admin / org owner / global admin still see it.
   */
  get showAddUsers(): boolean {
    if (this.authRealmService.hasAnyRole(PROJECT_ACTION_ADMIN_ROLES)) {
      return true;
    }
    if (this.isFoundationUser) {
      return false;
    }
    if (!this.authRealmService.hasRole("accello360-project-owner")) {
      return false;
    }
    return this.isCurrentUserProjectOwner();
  }

  /**
   * Project edit is allowed for org/global admins, or project owners on their own projects.
   */
  get showProjectEdit(): boolean {
    if (this.cardType !== "project") {
      return false;
    }
    if (this.authRealmService.hasAnyRole(PROJECT_ACTION_ADMIN_ROLES)) {
      return true;
    }
    if (!this.authRealmService.hasRole("accello360-project-owner")) {
      return false;
    }
    return this.isCurrentUserProjectOwner();
  }

  constructor() {
    this.userService.isFoundationUser$
      .pipe(takeUntilDestroyed())
      .subscribe((isFoundationUser) => {
        this.isFoundationUser = isFoundationUser;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["editDisabled"] && !this.editDisabled) {
      this.editClickLocked = false;
    }
  }

  private isCurrentUserProjectOwner(): boolean {
    const ownerEmail = this.cardData?.owner?.trim().toLowerCase();
    if (!ownerEmail) {
      return false;
    }
    const userEmail = (
      this.userService.snapshot?.email ||
      this.authRealmService.getUserProfile().email
    )
      ?.trim()
      .toLowerCase();
    return !!userEmail && userEmail === ownerEmail;
  }

  /**
   * Notifies the parent component of an action triggered within the card tile.
   * Emits an event containing the card data, an optional action type, and the originating event.
   * @param {ITileData} cardData - The data associated with the card tile.
   * @param {string} [actionType] - The type of action being triggered (e.g., 'create', 'edit').
   * @param {Event} [event] - The original DOM event, if applicable.
   */
  notifyParent(cardData: ITileData, actionType?: string, event?: Event) {
    if (actionType === "edit") {
      if (this.isEditLocked) {
        event?.stopPropagation();
        return;
      }
      this.editClickLocked = true;
    }
    if (!actionType && this.isEditLocked) {
      return;
    }
    this.onCreateProjectOrganization.emit({
      event: this.cardData,
      actionType,
      $event: event,
    });
  }
}
